import { ReactElement, useCallback } from 'react';
import { Stack } from '@mui/material';

import AddOrdersActions from '~components/AddOrdersActions';
import GeoError from '~components/GeoError';

import {
  DashboardLoadingWrapper,
  DashboardCompleteInitialConfigWrapper,
  DashboardWidgetsWrapper,
} from '../../../../Wrappers';

import DashboardVariantV2CarrierContextProvider, {
  useDashboardVariantV2CarrierContext,
} from './Context';
import DashboardVariantV2CarrierSelectDataProvider from './SelectDataContext';
import DashboardVariantV2CarrierCollectFiltersProvider from './CollectFiltersContext';
import DashboardVariantV2CarrierInWarehouseFiltersProvider, {
  useDashboardVariantV2CarrierInWarehouseFiltersContext,
} from './InWarehouseFiltersContext';
import DashboardVariantV2CarrierInTransitFiltersProvider from './InTransitFiltersContext';
import DashboardVariantV2CarrierFinalizedFiltersProvider from './FinalizedFiltersContext';
import DashboardVariantV2CarrierPresetsProvider from './PresetsContext';
import DashboardVariantV2CarrierHeader from './Header';
import DashboardVariantV2CarrierContent from './Content';

const DashboardVariantV2Carrier = (): ReactElement => {
  const {
    activeTab: { getIsActiveTab, setActiveTab },
    statistics: {
      loading: loadingStatistics,
      data: statistics,
      reloadData: reloadStatistics,
    },
    dialogs: {
      importFromFile: {
        open: openDialogImportFromFile,
        toggleOpen: toggleOpenDialogImportFromFile,
      },
      importFromIntegration: {
        open: openDialogImportFromIntegration,
        toggleOpen: toggleOpenDialogImportFromIntegration,
      },
      importFromAssociatedCompany: {
        open: openDialogImportFromAssociatedCompany,
        toggleOpen: toggleOpenDialogImportFromAssociatedCompany,
      },
      updateGeoError: {
        open: openDialogUpdateGeoError,
        toggleOpen: toogleOpenDialogUpdateGeoError,
      },
    },
  } = useDashboardVariantV2CarrierContext();

  const {
    forceRequest: { toggleValue: toogleForceRequest },
  } = useDashboardVariantV2CarrierInWarehouseFiltersContext();

  const handleReloadInWarehouseRequest = useCallback(() => {
    if (getIsActiveTab('inWarehouse')) {
      toogleForceRequest(true);
      return;
    }

    setActiveTab('inWarehouse');
  }, [getIsActiveTab, setActiveTab, toogleForceRequest]);

  return (
    <DashboardLoadingWrapper loading={loadingStatistics}>
      <DashboardCompleteInitialConfigWrapper
        vehiclesCount={statistics.companyInfo.quantityVehicles}
        warehousesCount={statistics.companyInfo.quantityWarehouses}
        onComplete={() => reloadStatistics(true)}
      >
        <DashboardWidgetsWrapper>
          <Stack spacing={2} flex={1}>
            <DashboardVariantV2CarrierHeader />

            <DashboardVariantV2CarrierContent />

            <AddOrdersActions.ImportDialogs
              dialogImportFromFile={{
                open: openDialogImportFromFile,
                onClose: () => toggleOpenDialogImportFromFile(false),
                onUpdateErrors: () => toogleOpenDialogUpdateGeoError(true),
              }}
              dialogImportFromIntegration={{
                open: openDialogImportFromIntegration,
                onClose: () => toggleOpenDialogImportFromIntegration(false),
              }}
              dialogImportFromAssociatedCompany={{
                open: openDialogImportFromAssociatedCompany,
                onClose: () =>
                  toggleOpenDialogImportFromAssociatedCompany(false),
              }}
              onSubmitImportSuccess={handleReloadInWarehouseRequest}
            />

            {openDialogUpdateGeoError && (
              <GeoError.UpdateDialog
                type="item"
                open={openDialogUpdateGeoError}
                onClose={() => toogleOpenDialogUpdateGeoError(false)}
                onSuccessSubmit={handleReloadInWarehouseRequest}
              />
            )}
          </Stack>
        </DashboardWidgetsWrapper>
      </DashboardCompleteInitialConfigWrapper>
    </DashboardLoadingWrapper>
  );
};

const DashboardVariantV2CarrierProviderWrapper = (): ReactElement => (
  <DashboardVariantV2CarrierContextProvider>
    <DashboardVariantV2CarrierSelectDataProvider>
      <DashboardVariantV2CarrierCollectFiltersProvider>
        <DashboardVariantV2CarrierInWarehouseFiltersProvider>
          <DashboardVariantV2CarrierInTransitFiltersProvider>
            <DashboardVariantV2CarrierFinalizedFiltersProvider>
              <DashboardVariantV2CarrierPresetsProvider>
                <DashboardVariantV2Carrier />
              </DashboardVariantV2CarrierPresetsProvider>
            </DashboardVariantV2CarrierFinalizedFiltersProvider>
          </DashboardVariantV2CarrierInTransitFiltersProvider>
        </DashboardVariantV2CarrierInWarehouseFiltersProvider>
      </DashboardVariantV2CarrierCollectFiltersProvider>
    </DashboardVariantV2CarrierSelectDataProvider>
  </DashboardVariantV2CarrierContextProvider>
);

export default DashboardVariantV2CarrierProviderWrapper;
